import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { apiClient } from '../api/client';

interface Settings {
  common_headers: Record<string, string>;
  common_user_agents: string[];
}

export const CommonHeadersUserAgents: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    common_headers: {},
    common_user_agents: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<Settings>('/api/v1/scraper/settings/');
      setSettings(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await apiClient.put('/api/v1/scraper/settings/', settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addHeader = () => {
    setSettings({
      ...settings,
      common_headers: { ...settings.common_headers, '': '' },
    });
  };

  const updateHeader = (oldKey: string, newKey: string, value: string) => {
    const newHeaders = { ...settings.common_headers };
    if (oldKey !== newKey) {
      delete newHeaders[oldKey];
    }
    if (newKey && value) {
      newHeaders[newKey] = value;
    } else if (!newKey || !value) {
      delete newHeaders[newKey];
    }
    setSettings({ ...settings, common_headers: newHeaders });
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...settings.common_headers };
    delete newHeaders[key];
    setSettings({ ...settings, common_headers: newHeaders });
  };

  const addUserAgent = () => {
    setSettings({
      ...settings,
      common_user_agents: [...settings.common_user_agents, ''],
    });
  };

  const updateUserAgent = (index: number, value: string) => {
    const newUserAgents = [...settings.common_user_agents];
    newUserAgents[index] = value;
    setSettings({ ...settings, common_user_agents: newUserAgents });
  };

  const removeUserAgent = (index: number) => {
    const newUserAgents = settings.common_user_agents.filter((_, i) => i !== index);
    setSettings({ ...settings, common_user_agents: newUserAgents });
  };

  if (loading) {
    return (
      <Box>
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Common Headers & User Agents</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Common Headers Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Common HTTP Headers</Typography>
                <IconButton color="primary" onClick={addHeader} size="small">
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These headers will be used by default for all marketplace definitions. Individual
                marketplaces can add additional headers that will override these.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {Object.entries(settings.common_headers).map(([key, value], index) => (
                <Box
                  key={`${key}-${index}`}
                  sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}
                >
                  <TextField
                    label="Header Name"
                    value={key}
                    onChange={e => updateHeader(key, e.target.value, value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Header Value"
                    value={value}
                    onChange={e => updateHeader(key, key, e.target.value)}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <IconButton color="error" onClick={() => removeHeader(key)} size="small">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {Object.keys(settings.common_headers).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No headers configured. Click the + button to add one.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Common User Agents Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Common User Agents</Typography>
                <IconButton color="primary" onClick={addUserAgent} size="small">
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These user agents will be rotated randomly for all marketplace definitions.
                Individual marketplaces can add additional user agents.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {settings.common_user_agents.map((ua, index) => (
                <Box key={index} sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label={`User Agent ${index + 1}`}
                    value={ua}
                    onChange={e => updateUserAgent(index, e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={2}
                  />
                  <IconButton color="error" onClick={() => removeUserAgent(index)} size="small">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {settings.common_user_agents.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No user agents configured. Click the + button to add one.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
